import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTools, 
    faCog, 
    faWrench,
    faRefresh
} from '@fortawesome/free-solid-svg-icons';
import { storageHelper } from '../../Helpers/storageHelper';
import { useNavigate } from 'react-router-dom';

const MaintenanceMode = () => {
    const navigate = useNavigate();
    const [maintenanceMessage, setMaintenanceMessage] = useState('نعتذر عن الإزعاج، نحن نقوم بتحسين الخدمة لكم');
    
    useEffect(() => {
        // Get maintenance message from localStorage if available
        const storedMessage = storageHelper.getItem('maintenanceMessage');
        if (storedMessage) {
            setMaintenanceMessage(storedMessage);
            // Clear the message from localStorage after reading it
            storageHelper.removeItem('maintenanceMessage');
        }
    }, []);

    const handleRefresh = () => {
        navigate('/');
    };

    return (
        <section className="maintenance-section">
            <motion.div 
                className="maintenance-container"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
            >
                <motion.div 
                    className="maintenance-icon"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                >
                    <FontAwesomeIcon icon={faCog} />
                </motion.div>

                <motion.div 
                    className="maintenance-content"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.8 }}
                >
                    <h1>نحن في صيانة</h1>
                    <p>{maintenanceMessage}</p>
                </motion.div>

                <motion.div 
                    className="maintenance-features"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                >
                    <div className="feature-item">
                        <FontAwesomeIcon icon={faTools} className="feature-icon" />
                        <span>تحسين الأداء</span>
                    </div>
                    <div className="feature-item">
                        <FontAwesomeIcon icon={faWrench} className="feature-icon" />
                        <span>إصلاح الأخطاء</span>
                    </div>
                    <div className="feature-item">
                        <FontAwesomeIcon icon={faCog} className="feature-icon" />
                        <span>تحديث النظام</span>
                    </div>
                </motion.div>

                <motion.button 
                    className="refresh-btn"
                    onClick={handleRefresh}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.8 }}
                >
                    <FontAwesomeIcon icon={faRefresh} />
                    العودة للصفحة الرئيسية
                </motion.button>

                <motion.div 
                    className="maintenance-info"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1, duration: 0.8 }}
                >
                    <p>سيتم إعادة الخدمة قريباً</p>
                    <p>شكراً لصبركم</p>
                </motion.div>
            </motion.div>

            <style jsx>{`
                .maintenance-section {
                    min-height: 100vh;
                    background: #f8fafc;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-family: 'Cairo', 'Tajawal', Arial, sans-serif;
                    padding: 20px;
                }

                .maintenance-container {
                    background: #fff;
                    border-radius: 20px;
                    padding: 60px 40px;
                    text-align: center;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
                    border: 2px solid #e0e7ef;
                    max-width: 500px;
                    width: 100%;
                }

                .maintenance-icon {
                    width: 80px;
                    height: 80px;
                    background: #f1f5f9;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 30px auto;
                    font-size: 2rem;
                    color: #2563eb;
                }

                .maintenance-content h1 {
                    font-size: 2rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 15px;
                }

                .maintenance-content p {
                    color: #64748b;
                    font-size: 1.1rem;
                    margin-bottom: 30px;
                    line-height: 1.6;
                }

                .maintenance-features {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                    margin-bottom: 30px;
                }

                .feature-item {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    padding: 12px;
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 1px solid #e0e7ef;
                }

                .feature-icon {
                    color: #2563eb;
                    font-size: 1.2rem;
                }

                .feature-item span {
                    color: #1e293b;
                    font-weight: 500;
                    font-size: 1rem;
                }

                .refresh-btn {
                    background: #2563eb;
                    color: #fff;
                    border: 2px solid #2563eb;
                    border-radius: 12px;
                    padding: 16px 32px;
                    font-size: 1.1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin: 0 auto 20px auto;
                }

                .refresh-btn:hover {
                    background: #1d4ed8;
                    border-color: #1d4ed8;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(37,99,235,0.2);
                }

                .maintenance-info {
                    margin-top: 20px;
                }

                .maintenance-info p {
                    color: #64748b;
                    font-size: 0.95rem;
                    margin: 5px 0;
                }

                @media (max-width: 600px) {
                    .maintenance-container {
                        padding: 40px 20px;
                        margin: 20px;
                    }

                    .maintenance-content h1 {
                        font-size: 1.5rem;
                    }

                    .maintenance-content p {
                        font-size: 1rem;
                    }

                    .refresh-btn {
                        font-size: 1rem;
                        padding: 14px 24px;
                    }
                }
            `}</style>
        </section>
    );
};

export default MaintenanceMode; 